import { Repeat, UserCheck, Users, UserX } from 'lucide-react';
import {
  DataTable,
  DateRangeFilter,
  DocNote,
  ExportButton,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  Pager,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatCount, formatRate } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import type { AdminProfileRow, DirectoryFilters, ProfileSortKey } from '../profiles/api';
import { useExportProfiles, useProfileCount, useProfileDirectory } from '../profiles/queries';
import {
  EATER_SEGMENTS,
  ProfileCell,
  RatingCell,
  SEGMENT_LABEL,
  SEGMENT_RANGE,
  SEGMENT_TONE,
  dayRangeParam,
  listParam,
  pickParam,
  segmentOf,
  stringParam,
} from '../profiles/people';
import { DetailDialogs } from '../orders/DetailDialogs';
import { EATER_TABS, EaterDialog } from './EaterDialog';

const UNREGISTERED = 'unregistered';
const DEMO = 'demo';

/** Started signup, never finished: no name or activity yet — contact details and when they started. */
const UNREGISTERED_COLUMNS: DataTableColumn<AdminProfileRow>[] = [
  { id: 'phone', header: 'Phone', width: '1.2fr', cell: (r) => <span className="truncate">{r.phone || '—'}</span> },
  {
    id: 'email',
    header: 'Email',
    width: '1.8fr',
    cell: (r) => <span className="truncate text-muted">{r.email || '—'}</span>,
  },
  {
    id: 'joined',
    header: 'Started signup',
    width: '1fr',
    sortKey: 'joinedAt',
    cell: (r) => <span className="text-muted">{formatDate(r.joinedAt)}</span>,
  },
];

const COLUMNS: DataTableColumn<AdminProfileRow>[] = [
  {
    id: 'eater',
    header: 'Eater',
    width: '1.9fr',
    sortKey: 'displayName',
    cell: (r) => <ProfileCell name={r.displayName} />,
  },
  { id: 'email', header: 'Email', width: '1.6fr', cell: (r) => <span className="text-muted">{r.email ?? '—'}</span> },
  {
    id: 'joined',
    header: 'Joined',
    width: '1fr',
    sortKey: 'joinedAt',
    cell: (r) => <span className="text-muted">{formatDate(r.joinedAt)}</span>,
  },
  {
    id: 'orders',
    header: 'Orders',
    width: '88px',
    align: 'right',
    numeric: true,
    sortKey: 'orderCount',
    cell: (r) => formatCount(r.orderCount),
  },
  {
    id: 'rating',
    header: 'Rating',
    width: '92px',
    sortKey: 'eaterAvgRating',
    cell: (r) => <RatingCell rating={r.eaterAvgRating} />,
  },
  {
    id: 'segment',
    header: 'Segment',
    width: '158px',
    cell: (r) => {
      const s = segmentOf(r.orderCount);
      return (
        <StatusPill tone={SEGMENT_TONE[s]} dot>
          {SEGMENT_LABEL[s]}
        </StatusPill>
      );
    },
  },
];

/**
 * Eater management (kit `EatersScreen`; FE plan §7 Eaters). Every consumer
 * profile. Segments are LIFETIME order-count ranges (Never = 0, Single = 1,
 * Repeat = 2+) and are labelled so — the analytics repeat-purchase rate is a
 * rolling window and is never shown as the same number (Q9).
 *
 * No last-active filter or column: the directory's `lastActiveAt` is the last
 * PUBLISH, which says nothing about an eater.
 *
 * "Unregistered" is a separate view of people who started signing up but
 * never finished (`registered=false`); every other view is registered only.
 */
export function EatersScreen() {
  const table = useTableUrlState();
  const { profile } = useSession();
  const f = table.filters;

  const unregistered = f.view === UNREGISTERED;
  // Test / demo accounts: left out of every other view, listed only here.
  const demo = f.view === DEMO;
  const segment = unregistered || demo ? undefined : pickParam(f.segment, EATER_SEGMENTS);
  const localeIds = listParam(f.localeIds);
  const signup = dayRangeParam(f.signupStart, f.signupEnd);

  const filters: DirectoryFilters = {
    ...(segment ? SEGMENT_RANGE[segment] : {}),
    demo,
    localeIds,
    searchTerm: table.q,
    signupStart: signup.start,
    signupEnd: signup.end,
    sortBy: table.sort?.key as ProfileSortKey | undefined,
    order: table.sort?.dir,
  };

  // Started signup, never finished: no orders, so no segment.
  const unregisteredFilters: DirectoryFilters = {
    unregistered: true,
    demo: false,
    localeIds,
    searchTerm: table.q,
    signupStart: signup.start,
    signupEnd: signup.end,
    sortBy: table.sort?.key as ProfileSortKey | undefined,
    order: table.sort?.dir,
  };

  const list = useProfileDirectory({
    ...(unregistered ? unregisteredFilters : filters),
    page: table.page,
    pageSize: table.pageSize,
  });
  const exportCsv = useExportProfiles('eaters');
  const eaterId = stringParam(f.eater);
  const openEater = (id: string) => table.setParams({ eater: id, eaterTab: undefined });
  const setSegment = (s: (typeof EATER_SEGMENTS)[number] | undefined) =>
    table.setParams({ view: undefined, segment: s, page: undefined });

  return (
    <div className="flex flex-col gap-[18px]">
      <EaterKpis localeIds={localeIds} />

      <div>
        <FilterBar>
          <SelectableChip active={!unregistered && !demo && !segment} onClick={() => setSegment(undefined)}>
            All
          </SelectableChip>
          {EATER_SEGMENTS.map((s) => (
            <SelectableChip key={s} active={segment === s} onClick={() => setSegment(s)}>
              {SEGMENT_LABEL[s]}
            </SelectableChip>
          ))}
          <FilterDivider />
          <SelectableChip
            active={unregistered}
            onClick={() => table.setParams({ view: UNREGISTERED, segment: undefined, page: undefined })}
          >
            Unregistered
          </SelectableChip>
          <SelectableChip
            active={demo}
            onClick={() => table.setParams({ view: DEMO, segment: undefined, page: undefined })}
          >
            Test/Demo
          </SelectableChip>
          <FilterDivider />
          <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
          <FilterDivider />
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-muted">Joined</span>
            <DateRangeFilter
              value={signup}
              timeZoneLabel={profile?.timezone}
              onChange={(r) => table.setParams({ signupStart: r.start, signupEnd: r.end, page: undefined })}
            />
          </div>
          {!unregistered && !demo && (
            <div className="ml-auto">
              <ExportButton onExport={() => exportCsv.mutateAsync(filters).catch(() => undefined)} />
            </div>
          )}
        </FilterBar>

        {unregistered ? (
          <>
            <DocNote>
              People who started signing up but never finished — no name, no dishes, no orders yet. They are left out of
              every other list.
            </DocNote>
            <DataTable
              label="Unregistered"
              minWidth={720}
              columns={UNREGISTERED_COLUMNS}
              rows={list.data?.items ?? []}
              getRowId={(r) => r.profileId}
              onRowClick={(r) => openEater(r.profileId)}
              loading={list.isPending}
              error={list.error}
              onRetry={() => void list.refetch()}
              sort={table.sort}
              onSortChange={table.setSort}
              emptyLabel="No unfinished signups."
            />
          </>
        ) : (
          <DataTable
            label={demo ? 'Test/Demo' : 'Eaters'}
            minWidth={1130}
            columns={COLUMNS}
            rows={list.data?.items ?? []}
            getRowId={(r) => r.profileId}
            onRowClick={(r) => openEater(r.profileId)}
            loading={list.isPending}
            error={list.error}
            onRetry={() => void list.refetch()}
            sort={table.sort}
            onSortChange={table.setSort}
            emptyLabel={demo ? 'No Test/Demo accounts.' : 'No eaters match those filters.'}
          />
        )}
        <Pager
          page={table.page}
          pageSize={table.pageSize}
          total={list.data?.total ?? 0}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          noun={unregistered ? 'signups' : 'eaters'}
        />
      </div>

      {eaterId && (
        <EaterDialog
          id={eaterId}
          tab={pickParam(f.eaterTab, EATER_TABS) ?? 'profile'}
          onTab={(t) => table.setParams({ eaterTab: t === 'profile' ? undefined : t })}
          onClose={() => table.setParams({ eater: undefined, eaterTab: undefined })}
        />
      )}
      <DetailDialogs />
    </div>
  );
}

/** KPI tiles via pageSize=1 count reads (until BE ask B10); they follow the locale filter. */
function EaterKpis({ localeIds }: { localeIds: string[] }) {
  const total = useProfileCount({ localeIds, demo: false });
  const repeat = useProfileCount({ localeIds, demo: false, ...SEGMENT_RANGE.repeat });
  const single = useProfileCount({ localeIds, demo: false, ...SEGMENT_RANGE.single });
  const never = useProfileCount({ localeIds, demo: false, ...SEGMENT_RANGE.never });
  const share = (n: number | undefined) =>
    n !== undefined && total.data ? `${formatRate(n / total.data, 'fraction', 0)} of registered` : undefined;

  return (
    <MetricRow>
      <MetricCard
        label="Registered eaters"
        value={formatCount(total.data)}
        hint="all consumer profiles"
        accent="var(--admin-chart-2)"
        icon={<Users size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Repeat purchasers"
        value={formatCount(repeat.data)}
        hint={share(repeat.data) && `${share(repeat.data)} · 2+ orders, lifetime`}
        accent="var(--admin-chart-3)"
        icon={<Repeat size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Single order"
        value={formatCount(single.data)}
        hint={share(single.data) && `${share(single.data)} · lifetime`}
        accent="var(--admin-chart-1)"
        icon={<UserCheck size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Never ordered"
        value={formatCount(never.data)}
        hint={share(never.data)}
        accent="var(--admin-chart-4)"
        icon={<UserX size={15} aria-hidden="true" />}
      />
    </MetricRow>
  );
}
